import { useMemo, useState } from "react";
import client from "../api/client";
import * as XLSX from "xlsx";

const styles = {
  page: { fontSize: 11 },
  card: {
    border: 0,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
    marginBottom: 10,
    background: "#fff",
  },
  header: (bg) => ({
    background: bg,
    color: "#fff",
    padding: "8px 12px",
  }),
  title: { fontSize: 12, fontWeight: 700, margin: 0 },
  sub: { fontSize: 10, opacity: 0.85 },
  body: { padding: 10 },
  label: { fontSize: 10, fontWeight: 700, marginBottom: 3, color: "#495057" },
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: { height: 30, fontSize: 11, borderRadius: 7, fontWeight: 600 },
  th: {
    fontSize: 10,
    padding: "7px 8px",
    whiteSpace: "nowrap",
    color: "#fff",
    background: "#07111f",
  },
  td: {
    fontSize: 11,
    padding: "6px 8px",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
  stat: {
    borderRadius: 12,
    padding: 10,
    background: "#f8f9fa",
    border: "1px solid #e9ecef",
    height: "100%",
  },
};

const get = (o, pascal, camel) => {
  const value = o?.[pascal] ?? o?.[camel] ?? "";
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return "";
  return value;
};

const num = (v) => {
  if (v === null || v === undefined || typeof v === "object") return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const fmt = (v) => num(v).toFixed(2);

const taxableValue = (r) =>
  num(get(r, "TaxableValue", "taxableValue")) ||
  num(get(r, "TaxableAmount", "taxableAmount")) ||
  num(get(r, "OutwardTaxableValue", "outwardTaxableValue"));

const taxAmount = (r) => {
  const direct =
    num(get(r, "TaxAmount", "taxAmount")) ||
    num(get(r, "TotalTax", "totalTax")) ||
    num(get(r, "OutwardTax", "outwardTax"));

  if (direct) return direct;

  return (
    num(get(r, "CGST", "cgst")) +
    num(get(r, "SGST", "sgst")) +
    num(get(r, "IGST", "igst"))
  );
};

const invoiceValue = (r) => {
  const direct =
    num(get(r, "InvoiceValue", "invoiceValue")) ||
    num(get(r, "GrandTotal", "grandTotal")) ||
    num(get(r, "InvoiceAmount", "invoiceAmount"));

  if (direct) return direct;

  return taxableValue(r) + taxAmount(r);
};

const cleanRowsForExport = (rows) =>
  rows.map((r) => ({
    ...r,
    TaxableValue: taxableValue(r),
    TaxAmount: taxAmount(r),
    InvoiceValue: invoiceValue(r),
  }));

export default function GstEnhancementsPage() {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(false);

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [classification, setClassification] = useState([]);
  const [reverseCharge, setReverseCharge] = useState([]);

  const buildParams = () => {
    const companyId = Number(localStorage.getItem("companyId") || 0);
    const params = { fromDate, toDate };
    if (companyId > 0) params.companyId = companyId;
    return params;
  };

  const load = async () => {
    if (!fromDate || !toDate) {
      alert("Select from and to date");
      return;
    }

    try {
      setLoading(true);

      const params = buildParams();

      const [r1, r3b, cls, rcm] = await Promise.all([
        client.get("/gst-enhancements/gstr1-enhanced", { params }),
        client.get("/gst-enhancements/gstr3b-enhanced", { params }),
        client.get("/gst-enhancements/classification-summary", { params }),
        client.get("/gst-enhancements/reverse-charge-summary", { params }),
      ]);

      setRows(Array.isArray(r1.data) ? r1.data : []);
      setSummary(r3b.data || null);
      setClassification(Array.isArray(cls.data) ? cls.data : []);
      setReverseCharge(Array.isArray(rcm.data) ? rcm.data : []);
    } catch (e) {
      alert(
        e?.response?.data?.message ||
          e?.response?.data ||
          "Failed to load GST enhancement reports",
      );
    } finally {
      setLoading(false);
    }
  };

  const totals = useMemo(
    () => ({
      taxable: rows.reduce((s, r) => s + taxableValue(r), 0),
      cgst: rows.reduce((s, r) => s + num(get(r, "CGST", "cgst")), 0),
      sgst: rows.reduce((s, r) => s + num(get(r, "SGST", "sgst")), 0),
      igst: rows.reduce((s, r) => s + num(get(r, "IGST", "igst")), 0),
      invoice: rows.reduce((s, r) => s + invoiceValue(r), 0),
      rcm: reverseCharge.reduce((s, r) => {
        const direct = num(get(r, "ReverseChargeTax", "reverseChargeTax"));
        return s + (direct || taxAmount(r));
      }, 0),
    }),
    [rows, reverseCharge],
  );

  const gstr3b = useMemo(() => {
    const outwardTaxable = num(
      get(summary, "OutwardTaxableValue", "outwardTaxableValue"),
    );

    const outputCGST =
      num(get(summary, "OutputCGST", "outputCGST")) ||
      num(get(summary, "OutwardCGST", "outwardCGST"));

    const outputSGST =
      num(get(summary, "OutputSGST", "outputSGST")) ||
      num(get(summary, "OutwardSGST", "outwardSGST"));

    const outputIGST =
      num(get(summary, "OutputIGST", "outputIGST")) ||
      num(get(summary, "OutwardIGST", "outwardIGST"));

    const itcCGST = num(get(summary, "ITCCGST", "itcCGST"));
    const itcSGST = num(get(summary, "ITCSGST", "itcSGST"));
    const itcIGST = num(get(summary, "ITCIGST", "itcIGST"));

    const inputTaxCredit =
      num(get(summary, "TotalInputTaxCredit", "totalInputTaxCredit")) ||
      num(get(summary, "InputTaxCredit", "inputTaxCredit")) ||
      itcCGST + itcSGST + itcIGST;

    const reverseChargeTax =
      num(get(summary, "ReverseChargeTax", "reverseChargeTax")) || totals.rcm;

    const grossPayable =
      outputCGST + outputSGST + outputIGST + reverseChargeTax - inputTaxCredit;

    return {
      outwardTaxable,
      outputCGST,
      outputSGST,
      outputIGST,
      inputTaxCredit,
      reverseChargeTax,
      netTaxPayable: Math.max(grossPayable, 0),
      itcCarryForward: Math.max(-grossPayable, 0),
    };
  }, [summary, totals.rcm]);

  const exportExcel = () => {
    if (!rows.length && !classification.length && !reverseCharge.length) {
      alert("No GST data to export");
      return;
    }

    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(cleanRowsForExport(rows)),
      "GSTR1 Enhanced",
    );

    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(cleanRowsForExport(classification)),
      "Classification",
    );

    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(cleanRowsForExport(reverseCharge)),
      "Reverse Charge",
    );

    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet([
        {
          OutwardTaxable: gstr3b.outwardTaxable,
          OutputCGST: gstr3b.outputCGST,
          OutputSGST: gstr3b.outputSGST,
          OutputIGST: gstr3b.outputIGST,
          InputTaxCredit: gstr3b.inputTaxCredit,
          ReverseChargeTax: gstr3b.reverseChargeTax,
          NetTaxPayable: gstr3b.netTaxPayable,
          ITCCarryForward: gstr3b.itcCarryForward,
        },
      ]),
      "GSTR3B Summary",
    );

    XLSX.writeFile(wb, `GST_Enhanced_${fromDate}_to_${toDate}.xlsx`);
  };

  return (
    <div className="container-fluid py-2" style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header("linear-gradient(90deg,#0b4f4a,#0f766e)")}>
          <h6 style={styles.title}>
            <i className="bi bi-receipt-cutoff me-2"></i>
            GST Enhancements
          </h6>
          <div style={styles.sub}>
            CGST/SGST/IGST, GST classification, reverse charge and enhanced GST
            reporting
          </div>
        </div>

        <div style={styles.body}>
          <div className="row g-2 align-items-end">
            <div className="col-md-3">
              <label style={styles.label}>From Date</label>
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>

            <div className="col-md-3">
              <label style={styles.label}>To Date</label>
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>

            <div className="col-md-6 d-flex gap-2">
              <button
                className="btn btn-primary btn-sm"
                style={styles.btn}
                onClick={load}
                disabled={loading}
              >
                <i className="bi bi-search me-1"></i>
                {loading ? "Loading..." : "Load GST"}
              </button>

              <button
                className="btn btn-success btn-sm"
                style={styles.btn}
                onClick={exportExcel}
              >
                <i className="bi bi-file-earmark-excel me-1"></i>
                Export Excel
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-2 mb-2">
        {[
          ["Taxable", totals.taxable],
          ["CGST", totals.cgst],
          ["SGST", totals.sgst],
          ["IGST", totals.igst],
          ["Invoice Value", totals.invoice],
          ["RCM Tax", gstr3b.reverseChargeTax],
          ["ITC Carry Forward", gstr3b.itcCarryForward],
        ].map(([k, v]) => (
          <div className="col-md" key={k}>
            <div style={styles.stat}>
              <div className="text-muted" style={{ fontSize: 10 }}>
                {k}
              </div>
              <div className="fw-bold text-primary" style={{ fontSize: 15 }}>
                ₹ {fmt(v)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {summary && (
        <div style={styles.card}>
          <div style={styles.header("linear-gradient(90deg,#0b4f4a,#0f766e)")}>
            <h6 style={styles.title}>Enhanced GSTR-3B Summary</h6>
          </div>

          <div style={styles.body}>
            <div className="table-responsive">
              <table className="table table-sm table-bordered mb-0">
                <tbody>
                  {[
                    ["Outward Taxable", gstr3b.outwardTaxable],
                    ["Output CGST", gstr3b.outputCGST],
                    ["Output SGST", gstr3b.outputSGST],
                    ["Output IGST", gstr3b.outputIGST],
                    ["Input Tax Credit", gstr3b.inputTaxCredit],
                    ["Reverse Charge Tax", gstr3b.reverseChargeTax],
                    ["Net Tax Payable", gstr3b.netTaxPayable],
                    ["ITC Carry Forward", gstr3b.itcCarryForward],
                  ].map(([k, v]) => (
                    <tr key={k}>
                      <th style={styles.th}>{k}</th>
                      <td
                        className={`text-end fw-bold ${
                          k === "Net Tax Payable"
                            ? "text-success"
                            : k === "ITC Carry Forward"
                              ? "text-primary"
                              : ""
                        }`}
                        style={styles.td}
                      >
                        ₹ {fmt(v)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <div className="row g-2">
        <div className="col-lg-6">
          <MiniTable
            title="GST Classification Summary"
            icon="bi-tags"
            rows={classification}
            cols={[
              "Classification",
              "InvoiceCount",
              "TaxableValue",
              "CGST",
              "SGST",
              "IGST",
              "TaxAmount",
              "GrandTotal",
            ]}
          />
        </div>

        <div className="col-lg-6">
          <MiniTable
            title="Reverse Charge Summary"
            icon="bi-arrow-repeat"
            rows={reverseCharge}
            cols={[
              "VoucherNo",
              "VoucherDate",
              "SupplierName",
              "ITCEligibility",
              "IsReverseCharge",
              "TaxableValue",
              "CGST",
              "SGST",
              "IGST",
              "TaxAmount",
              "GrandTotal",
            ]}
          />
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.header("linear-gradient(90deg,#0b4f4a,#0f766e)")}>
          <h6 style={styles.title}>
            <i className="bi bi-table me-2"></i>
            Enhanced GSTR-1 / GST Register
          </h6>
        </div>

        <div style={styles.body}>
          <div style={{ overflowX: "auto", maxHeight: 380 }}>
            <table className="table table-sm table-bordered table-hover mb-0">
              <thead className="sticky-top" style={{ zIndex: 1 }}>
                <tr>
                  {[
                    "Invoice",
                    "Date",
                    "Party",
                    "GSTIN",
                    "Supply",
                    "GST Class",
                    "Taxable",
                    "CGST",
                    "SGST",
                    "IGST",
                    "Tax",
                    "Value",
                  ].map((h) => (
                    <th key={h} style={styles.th}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {rows.length ? (
                  rows.map((r, i) => (
                    <tr key={i}>
                      <td style={styles.td}>
                        {get(r, "InvoiceNo", "invoiceNo")}
                      </td>
                      <td style={styles.td}>
                        {String(get(r, "InvoiceDate", "invoiceDate")).slice(
                          0,
                          10,
                        )}
                      </td>
                      <td style={styles.td}>
                        {get(r, "PartyName", "partyName") ||
                          get(r, "CustomerName", "customerName")}
                      </td>
                      <td style={styles.td}>
                        {get(r, "GSTNo", "gstNo") ||
                          get(r, "CustomerGSTIN", "customerGSTIN") ||
                          "-"}
                      </td>
                      <td style={styles.td}>
                        {get(r, "SupplyType", "supplyType")}
                      </td>
                      <td style={styles.td}>
                        {get(r, "GSTSaleType", "gstSaleType") ||
                          get(r, "GSTClassification", "gstClassification") ||
                          "-"}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {fmt(taxableValue(r))}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {fmt(get(r, "CGST", "cgst"))}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {fmt(get(r, "SGST", "sgst"))}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {fmt(get(r, "IGST", "igst"))}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {fmt(taxAmount(r))}
                      </td>
                      <td className="text-end fw-semibold" style={styles.td}>
                        {fmt(invoiceValue(r))}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="12"
                      className="text-center text-muted"
                      style={{ padding: 20 }}
                    >
                      No GST enhancement records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function MiniTable({ title, icon, rows, cols }) {
  return (
    <div style={styles.card}>
      <div style={styles.header("linear-gradient(90deg,#0b4f4a,#0f766e)")}>
        <h6 style={styles.title}>
          <i className={`bi ${icon} me-2`}></i>
          {title}
        </h6>
      </div>

      <div style={styles.body}>
        <div style={{ overflowX: "auto", maxHeight: 260 }}>
          <table className="table table-sm table-bordered mb-0">
            <thead className="sticky-top">
              <tr>
                {cols.map((c) => (
                  <th key={c} style={styles.th}>
                    {c}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {rows.length ? (
                rows.map((r, i) => (
                  <tr key={i}>
                    {cols.map((c) => {
                      const lower = c.charAt(0).toLowerCase() + c.slice(1);
                      const value = get(r, c, lower);
                      const isAmount =
                        c.includes("Value") ||
                        c.includes("Tax") ||
                        c.includes("Total") ||
                        ["CGST", "SGST", "IGST"].includes(c);

                      const display =
                        c === "TaxableValue"
                          ? fmt(taxableValue(r))
                          : c === "TaxAmount"
                            ? fmt(taxAmount(r))
                            : c === "GrandTotal"
                              ? fmt(invoiceValue(r))
                              : isAmount
                                ? fmt(value)
                                : value || "-";

                      return (
                        <td
                          key={c}
                          className={isAmount ? "text-end" : ""}
                          style={styles.td}
                        >
                          {display}
                        </td>
                      );
                    })}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={cols.length}
                    className="text-center text-muted"
                    style={{ padding: 18 }}
                  >
                    No data
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}